import { type ThemeConfig, extendTheme } from "@chakra-ui/react";
import "@fontsource/roboto/cyrillic";
import "@fontsource/roboto/latin";
import "@fontsource/roboto/symbols";

const breakpoints = {
  base: "0em",
  sm: "400px",
  md: "1000px",
  lg: "1200px",
  xl: "1400px",
};

const fonts = {
  heading: "'Roboto', sans-serif",
  body: "'Roboto', sans-serif",
  main: "'Roboto', sans-serif",
};

const components = {
  Heading: {
    variants: {
      text_md_normal: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "24px",
      },
      text_md_bold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "16px",
        fontWeight: "700",
        lineHeight: "24px",
      },
      text_lg_bold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "20px",
        fontWeight: "700",
        lineHeight: "28px",
      },
      text_4xl_medium: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "36px",
        fontWeight: "700",
        lineHeight: "40px",
      },
    },
  },
  Text: {
    variants: {
      text_card_caption: {
        fontFamily: "'Fira Sans', sans-serif;",
        fontSize: "9px",
        fontWeight: "400",
        lineHeight: "9px",
      },
      text_card_support: {
        fontFamily: "'Fira Sans', sans-serif;",
        fontSize: "11px",
        fontWeight: "500",
        lineHeight: "13px",
      },
      text_card_text: {
        fontFamily: "'Fira Sans', sans-serif;",
        fontSize: "12px",
        fontWeight: "500",
        lineHeight: "16px",
      },
      text_card_button: {
        fontFamily: "'Fira Sans', sans-serif;",
        fontSize: "14px",
        fontWeight: "500",
        lineHeight: "20px",
      },
      text_card_header: {
        fontFamily: "'Fira Sans', sans-serif;",
        fontSize: "14px",
        fontWeight: "600",
        lineHeight: "18px",
      },
      text_card_amount: {
        fontFamily: "'Fira Sans', sans-serif;",
        fontSize: "19px",
        fontWeight: "500",
        lineHeight: "20px",
      },
      text_xxs__normal: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "11px",
        fontWeight: "500",
        lineHeight: "14px",
      },
      text_xs_normal: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "13px",
        fontWeight: "400",
        lineHeight: "16px",
      },
      text_xs_medium: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "13px",
        fontWeight: "500",
        lineHeight: "16px",
      },
      text_xs_semibold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "13px",
        fontWeight: "700",
        lineHeight: "16px",
      },
      text_sm_normal: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "20px",
      },
      text_sm_medium: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "14px",
        fontWeight: "500",
        lineHeight: "20px",
      },
      text_sm_bold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "14px",
        fontWeight: "700",
        lineHeight: "20px",
      },
      text_md_normal: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "24px",
      },
      text_md_semibold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "16px",
        fontWeight: "500",
        lineHeight: "24px",
      },
      text_md_bold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "16px",
        fontWeight: "700",
        lineHeight: "24px",
      },
      text_2xl_semibold: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "24px",
        fontWeight: "700",
        lineHeight: "32px",
      },
    },
  },
  Button: {
    baseStyle: {
      fontWeight: "400",
    },
  },
  Table: {
    baseStyle: {
      th: {
        textTransform: "initial",
      },
    },
    variants: {
      simple: {
        td: {
          px: 2.5,
          py: 1.5,
          textAlign: "left",
          fontSize: "sm",
          fontWeight: "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          borderBottom: "1px solid",
          borderColor: "gray-base-04",
          backgroundColor: "gray-base-02",
          verticalAlign: "top",
        },
        th: {
          py: 3,
          px: 2.5,
          fontSize: "0.8125rem",
          fontWeight: 600,
          lineHeight: "1.25rem",
          textTransform: "none",
          borderColor: "gray-base-04",
        },
        tr: {
          "&:last-of-type td": {
            borderBottom: "none",
          },
        },
      },
    },
  },
  Popover: {
    variants: {
      fullW: {
        popper: {
          width: "100%",
        },
        content: {
          width: "100%",
        },
      },
    },
  },
};

const spacing = {
  px: "1px",
  0.5: "0.125rem",
  1: "0.25rem",
  1.5: "0.375rem",
  2: "0.5rem",
  2.5: "0.625rem",
  3: "0.75rem",
  3.5: "0.875rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "1.75rem",
  8: "2rem",
  9: "2.25rem",
  10: "2.5rem",
  12: "3rem",
  14: "3.5rem",
  16: "4rem",
  20: "5rem",
  24: "6rem",
  28: "7rem",
  32: "8rem",
  36: "9rem",
  40: "10rem",
  44: "11rem",
  48: "12rem",
  52: "13rem",
  56: "14rem",
  60: "15rem",
  64: "16rem",
  72: "18rem",
  80: "20rem",
  96: "24rem",
};

const sizes = {
  max: "max-content",
  min: "min-content",
  full: "100%",
  minM: "400px",
  maxS: "500px",
  maxM: "1200px",
  "3xs": "14rem",
  "2xs": "16rem",
  xs: "20rem",
  sm: "24rem",
  md: "28rem",
  lg: "32rem",
  xl: "36rem",
  "2xl": "42rem",
  "3xl": "48rem",
  "4xl": "56rem",
  "5xl": "64rem",
  "6xl": "72rem",
  "7xl": "80rem",
  "8xl": "90rem",
  container: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
  },
};

const config: ThemeConfig = {
  initialColorMode: "dark",
  useSystemColorMode: true,
};

const theme = extendTheme({
  breakpoints,
  spacing,
  sizes,
  fonts,
  components,
  config,

  semanticTokens: {
    colors: {
      "brand.50": {
        default: "#cbe6e1",
        _dark: "#cbe6e1",
      },
      "brand.100": {
        default: "#acdcd3",
        _dark: "#acdcd3",
      },
      "brand.200": {
        default: "#8ad5c8",
        _dark: "#8ad5c8",
      },
      "brand.300": {
        default: "#65d2be",
        _dark: "#65d2be",
      },
      "brand.400": {
        default: "#3dd0b6",
        _dark: "#3dd0b6",
      },
      "brand.500": {
        default: "#1e9e87",
        _dark: "#1e9e87",
      },
      "brand.600": {
        default: "#009079",
        _dark: "#009079",
      },
      "brand.700": {
        default: "#00816c",
        _dark: "#00816c",
      },
      "brand.800": {
        default: "#00735f",
        _dark: "#00735f",
      },
      "brand.900": {
        default: "#00735f",
        _dark: "#00735f",
      },
      "brand-hover": {
        default: "#25c0a4",
      },
      "brand-active": {
        default: "#1b8e79",
      },
      "brand-bg-primary": {
        default: "rgba(37, 192, 164, 0.12)",
      },
      "brand.clear": {
        default: "rgba(34, 181, 154, .12)",
      },
      "elevation-bg": {
        default: "#fbfcff",
        _dark: "#1a1b1e",
      },
      "gray-base-01": {
        default: "#fbfcff",
        _dark: "#16171a",
      },
      "gray-base-02": {
        default: "#ffffff",
        _dark: "rgba(32, 33, 36)",
      },
      "gray-base-03": {
        default: "#f4f4f4",
        _dark: "rgba(26, 27, 30)",
      },
      "gray-base-04": {
        default: "#dddfe0",
        _dark: "rgba(221, 223, 224, .1)",
      },
      "gray-base-05": {
        default: "#c7c9cc",
        _dark: "#5c636b",
      },
      "gray-base-06": {
        default: "#9299a2",
        _dark: "#c7c9cc",
      },
      "gray-base-07": {
        default: "#5c636b",
        _dark: "#dddfe0",
      },
      "gray-base-08": {
        default: "#333333",
        _dark: "#f6f7f8",
      },
      "gray-base-09": {
        default: "#000000",
        _dark: "#ffffff",
      },
      "text-base-01": {
        default: "rgba(0, 0, 0, .9)",
        _dark: "#f5f8fe",
      },
      "text-base-02": {
        default: "rgba(0, 0, 0, .64)",
        _dark: "rgba(245, 248, 254, .72)",
      },
      "text-base-03": {
        default: "rgba(0, 0, 0, .5)",
        _dark: "rgba(245, 248, 254, .4)",
      },
      "red-error": {
        default: "red.500",
        _dark: "red.300",
      },
      "whiteAlpha.950": { default: "rgba(255, 255, 255, 0.95)" },
      "blackAlpha.950": { default: "rgba(0, 0, 0, 0.95)" },
      "gray.50": {
        default: "#fafafa",
      },
      "gray.100": {
        default: "#f4f4f5",
      },
      "gray.200": {
        default: "#e4e4e7",
      },
      "gray.300": {
        default: "#d4d4d8",
      },
      "gray.400": {
        default: "#a1a1aa",
      },
      "gray.500": {
        default: "#71717a",
      },
      "gray.600": {
        default: "#52525b",
      },
      "gray.700": {
        default: "#3f3f46",
      },
      "gray.800": {
        default: "#27272a",
      },
      "gray.900": {
        default: "#18181b",
      },
      "gray.950": {
        default: "#111111",
      },
      "red.50": { default: "#fef2f2" },
      "red.100": { default: "#fee2e2" },
      "red.200": { default: "#fecaca" },
      "red.300": { default: "#fca5a5" },
      "red.400": { default: "#f87171" },
      "red.500": { default: "#ef4444" },
      "red.600": { default: "#dc2626" },
      "red.700": { default: "#991919" },
      "red.800": { default: "#511111" },
      "red.900": { default: "#300c0c" },
      "red.950": { default: "#1f0808" },
      "orange.50": { default: "#fff7ed" },
      "orange.100": { default: "#ffedd5" },
      "orange.200": { default: "#fed7aa" },
      "orange.300": { default: "#fdba74" },
      "orange.400": { default: "#fb923c" },
      "orange.500": { default: "#f97316" },
      "orange.600": { default: "#ea580c" },
      "orange.700": { default: "#92310a" },
      "orange.800": { default: "#6c2710" },
      "orange.900": { default: "#3b1106" },
      "orange.950": { default: "#220a04" },
      "yellow.50": { default: "#fefce8" },
      "yellow.100": { default: "#fef9c3" },
      "yellow.200": { default: "#fef08a" },
      "yellow.300": { default: "#fde047" },
      "yellow.400": { default: "#facc15" },
      "yellow.500": { default: "#eab308" },
      "yellow.600": { default: "#ca8a04" },
      "yellow.700": { default: "#845209" },
      "yellow.800": { default: "#713f12" },
      "yellow.900": { default: "#422006" },
      "yellow.950": { default: "#281304" },
      "green.50": { default: "#f0fdf4" },
      "green.100": { default: "#dcfce7" },
      "green.200": { default: "#bbf7d0" },
      "green.300": { default: "#86efac" },
      "green.400": { default: "#4ade80" },
      "green.500": { default: "#22c55e" },
      "green.600": { default: "#16a34a" },
      "green.700": { default: "#116932" },
      "green.800": { default: "#124a28" },
      "green.900": { default: "#042713" },
      "green.950": { default: "#03190c" },
      "amber.50": { default: "#fffbeb" },
      "amber.200": { default: "#fee685" },
      "amber.300": { default: "#ffd230" },
      "amber.400": { default: "#ffb900" },
      "amber.500": { default: "#fe9a00" },
      "amber.600": { default: "#e17100" },
      "amber.700": { default: "#bb4d00" },
      "amber.800": { default: "#973c00" },
      "amber.900": { default: "#7b3306" },
      "amber.950": { default: "#461901" },
    },
  },
});

export default theme;
